import { useState, useEffect, useCallback, type JSX } from "react";
import { Link } from "react-router-dom";
import {
  BADGES,
  CHALLENGES_PER_LEVEL,
  MAX_STARS_PER_CHALLENGE,
  type DisciplineSlug,
} from "@jumpbean/shared";
import { Badge } from "./Badge";
import { StarDisplay } from "./StarDisplay";
import { CharacterImage, GLOW_COLORS, CHARACTER_DISPLAY_NAMES } from "./CharacterImage";
import { IntroVideoPlayer } from "./IntroVideoPlayer";

export interface LevelProgress {
  levelNumber: number;
  isUnlocked: boolean;
  isCompleted: boolean;
  starsEarned: number;
  maxStars: number;
  hasBadge: boolean;
  /** Reason why level is locked (if !isUnlocked) */
  lockReason?: "previous_incomplete" | "subscription_required";
}

export interface DisciplineLevelMapProps {
  /** The discipline to display */
  discipline: DisciplineSlug;
  /** Progress data for each level */
  levels: LevelProgress[];
  /** Called when a level is selected */
  onLevelSelect?: (levelNumber: number) => void;
  /** Whether to show the badge collection */
  showBadgeCollection?: boolean;
  /** Level that was just unlocked (for animation) */
  newlyUnlockedLevel?: number | null;
  /** Callback when unlock animation completes */
  onUnlockAnimationComplete?: () => void;
}

// World theme configurations
const WORLD_THEMES: Record<
  DisciplineSlug,
  {
    name: string;
    tagline: string;
    bgGradient: string;
    accentGradient: string;
    pathColor: string;
    levelIcon: string;
    completedIcon: string;
  }
> = {
  math: {
    name: "Starlight Castle",
    tagline: "Count the stars with Luna!",
    bgGradient: "from-[#1a1a2e] via-[#16213e] to-[#0f0f23]",
    accentGradient: "from-luna-light via-luna to-magic-400",
    pathColor: "#E8A0FF",
    levelIcon: "tower",
    completedIcon: "castle",
  },
  spelling: {
    name: "Fairy Garden",
    tagline: "Grow words with Petal!",
    bgGradient: "from-[#0f2f1f] via-[#1a3f2a] to-[#0a2015]",
    accentGradient: "from-petal-light via-petal to-meadow-400",
    pathColor: "#90EE90",
    levelIcon: "flower",
    completedIcon: "bloom",
  },
  patterns: {
    name: "Rainbow Bridge",
    tagline: "Find the patterns with Prism!",
    bgGradient: "from-[#1f1a2f] via-[#2a1f3f] to-[#150f25]",
    accentGradient: "from-prism-purple via-prism-blue to-prism-green",
    pathColor: "#A29BFE",
    levelIcon: "gem",
    completedIcon: "crystal",
  },
  typing: {
    name: "Keyboard Kingdom",
    tagline: "Cast letter spells with Whiskers!",
    bgGradient: "from-[#1a1a3e] via-[#16164e] to-[#0f0f33]",
    accentGradient: "from-whiskers-light via-whiskers to-sky-600",
    pathColor: "#4A90D9",
    levelIcon: "key",
    completedIcon: "scroll",
  },
  programming: {
    name: "Code Quest",
    tagline: "Build adventures with Pixel!",
    bgGradient: "from-[#2f2015] via-[#3f2a1a] to-[#25150a]",
    accentGradient: "from-pixel-light via-pixel to-sunshine-500",
    pathColor: "#FF8C42",
    levelIcon: "gear",
    completedIcon: "robot",
  },
};

/**
 * DisciplineLevelMap - A themed level progression map for any discipline
 *
 * Features:
 * - Character-themed background and decorations
 * - Magical path connecting levels
 * - Character floating at top
 * - Intro video preview
 * - Badge display for completed levels
 * - Sparkle animations on unlocked levels
 */
export function DisciplineLevelMap({
  discipline,
  levels,
  onLevelSelect,
  showBadgeCollection = false,
  newlyUnlockedLevel,
  onUnlockAnimationComplete,
}: DisciplineLevelMapProps) {
  const [animatingLevel, setAnimatingLevel] = useState<number | null>(null);
  const [showSparkles, setShowSparkles] = useState(false);

  const theme = WORLD_THEMES[discipline];
  const badges = BADGES[discipline] || [];
  const glowColor = GLOW_COLORS[discipline];
  const characterName = CHARACTER_DISPLAY_NAMES[discipline];

  // Handle unlock animation
  useEffect(() => {
    if (newlyUnlockedLevel) {
      setAnimatingLevel(newlyUnlockedLevel);
      setShowSparkles(true);

      const timer = setTimeout(() => {
        setAnimatingLevel(null);
        setShowSparkles(false);
        onUnlockAnimationComplete?.();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [newlyUnlockedLevel, onUnlockAnimationComplete]);

  const handleLevelClick = useCallback(
    (levelNumber: number, isUnlocked: boolean) => {
      if (isUnlocked && onLevelSelect) {
        onLevelSelect(levelNumber);
      }
    },
    [onLevelSelect]
  );

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Themed background */}
      <div className={`absolute inset-0 bg-gradient-to-b ${theme.bgGradient}`}>
        {/* Background decorations based on discipline */}
        <ThemedBackground discipline={discipline} />
      </div>

      {/* Main content */}
      <div className="relative z-10 px-4 py-8 max-w-lg mx-auto">
        {/* Header with character */}
        <div className="text-center mb-6">
          <div className="relative inline-block">
            <CharacterImage
              discipline={discipline}
              expression="happy"
              size="xl"
              floating
              glowing
            />
            {/* Sparkles around character */}
            <div className="absolute -top-2 -right-2">
              <Sparkle
                className="w-6 h-6"
                style={{ color: glowColor }}
                delay={0}
              />
            </div>
            <div className="absolute -bottom-1 -left-3">
              <Sparkle
                className="w-4 h-4"
                style={{ color: glowColor }}
                delay={0.3}
              />
            </div>
          </div>
          <h1
            className="mt-4 font-display font-extrabold text-display-md text-white"
            style={{
              textShadow: `0 2px 8px ${glowColor}80`,
            }}
          >
            {theme.name}
          </h1>
          <p className="mt-2 font-body text-body-lg text-white/70">
            {theme.tagline}
          </p>
        </div>

        {/* Intro Video Preview */}
        <div className="mb-8">
          <IntroVideoPlayer
            discipline={discipline}
            className="rounded-2xl shadow-2xl"
          />
        </div>

        {/* Level Path */}
        <div className="relative">
          {/* Magical path connection */}
          <svg
            className="absolute left-1/2 top-0 -translate-x-1/2 w-8 h-full pointer-events-none"
            viewBox="0 0 32 500"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient
                id={`pathGradient-${discipline}`}
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor={theme.pathColor} stopOpacity="0.8" />
                <stop offset="50%" stopColor="#FFD93D" stopOpacity="0.6" />
                <stop offset="100%" stopColor={theme.pathColor} stopOpacity="0.8" />
              </linearGradient>
              <filter id="pathGlow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              d="M16 0 Q16 100 16 100 Q16 200 16 200 Q16 300 16 300 Q16 400 16 400 Q16 500 16 500"
              fill="none"
              stroke={`url(#pathGradient-${discipline})`}
              strokeWidth="4"
              strokeDasharray="12 8"
              filter="url(#pathGlow)"
              className="animate-pulse"
            />
          </svg>

          {/* Start point decoration */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div
                className={`w-20 h-20 rounded-full bg-gradient-to-br ${theme.accentGradient} shadow-lg flex items-center justify-center`}
                style={{
                  boxShadow: `0 0 30px ${glowColor}80`,
                }}
              >
                <span className="text-3xl">✨</span>
              </div>
              <p
                className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap font-display font-bold text-body-lg text-white"
                style={{ textShadow: `0 2px 4px rgba(0,0,0,0.5)` }}
              >
                Start Here!
              </p>
            </div>
          </div>

          {/* Level cards */}
          <div className="flex flex-col gap-6 mt-12">
            {levels.map((level, index) => (
              <LevelCard
                key={level.levelNumber}
                discipline={discipline}
                level={level}
                badge={badges[index]}
                theme={theme}
                isAnimating={animatingLevel === level.levelNumber}
                showSparkles={showSparkles && animatingLevel === level.levelNumber}
                onClick={() => handleLevelClick(level.levelNumber, level.isUnlocked)}
              />
            ))}
          </div>
        </div>

        {/* Badge Collection */}
        {showBadgeCollection && (
          <div className="mt-16">
            <BadgeCollection discipline={discipline} levels={levels} badges={badges} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Individual level card
 */
interface LevelCardProps {
  discipline: DisciplineSlug;
  level: LevelProgress;
  badge?: { level: number; name: string; description: string };
  theme: (typeof WORLD_THEMES)[DisciplineSlug];
  isAnimating?: boolean;
  showSparkles?: boolean;
  onClick: () => void;
}

function LevelCard({
  discipline,
  level,
  badge,
  theme,
  isAnimating,
  showSparkles,
  onClick,
}: LevelCardProps) {
  const { levelNumber, isUnlocked, isCompleted, starsEarned, maxStars } = level;
  const glowColor = GLOW_COLORS[discipline];

  // Star rating (0-3)
  const starRating =
    maxStars > 0
      ? (Math.min(3, Math.max(0, Math.round((starsEarned / maxStars) * 3))) as
          | 0
          | 1
          | 2
          | 3)
      : 0;

  const cardContent = (
    <div
      className={`
        relative
        w-full max-w-xs mx-auto
        transition-all duration-500
        ${isAnimating ? "scale-110" : ""}
        ${isUnlocked ? "cursor-pointer" : "cursor-not-allowed"}
      `}
      onClick={isUnlocked ? onClick : undefined}
    >
      {/* Unlock animation sparkles */}
      {showSparkles && <UnlockSparkles color={glowColor} />}

      {/* Level card */}
      <div
        className={`
          relative
          p-5
          rounded-2xl
          border-3
          transition-all duration-300
          ${
            isCompleted
              ? `bg-gradient-to-br ${theme.accentGradient}/20 border-white/30`
              : isUnlocked
              ? "bg-white/10 backdrop-blur-sm border-white/20 hover:border-white/40"
              : "bg-neutral-900/40 border-neutral-700/50 opacity-60"
          }
        `}
        style={{
          boxShadow: isCompleted
            ? `0 0 30px ${glowColor}40`
            : isUnlocked
            ? `0 0 20px ${glowColor}20`
            : undefined,
        }}
      >
        <div className="flex items-center gap-4">
          {/* Level icon */}
          <div
            className={`
              relative
              w-16 h-16
              rounded-xl
              flex items-center justify-center
              transition-all duration-300
              ${
                isCompleted
                  ? `bg-gradient-to-br ${theme.accentGradient}`
                  : isUnlocked
                  ? `bg-gradient-to-br ${theme.accentGradient}/70`
                  : "bg-neutral-700"
              }
            `}
            style={{
              boxShadow: isUnlocked ? `0 4px 12px ${glowColor}40` : undefined,
            }}
          >
            {isCompleted ? (
              <LevelIcon type={theme.completedIcon} className="w-10 h-10 text-white" />
            ) : isUnlocked ? (
              <LevelIcon type={theme.levelIcon} className="w-10 h-10 text-white" />
            ) : (
              <LockIcon className="w-8 h-8 text-neutral-500" />
            )}

            {/* Level number badge */}
            {isUnlocked && (
              <div
                className={`
                  absolute -top-2 -right-2
                  w-7 h-7
                  rounded-full
                  flex items-center justify-center
                  font-display font-bold text-sm
                  ${
                    isCompleted
                      ? "bg-sunshine-500 text-neutral-900"
                      : "bg-white/90 text-neutral-800"
                  }
                  shadow-md
                `}
              >
                {levelNumber}
              </div>
            )}

            {/* Pulse animation for unlocked levels */}
            {isUnlocked && !isCompleted && (
              <div className="absolute inset-0 animate-pulse">
                <div
                  className="absolute inset-0 rounded-xl"
                  style={{ background: `${glowColor}20` }}
                />
              </div>
            )}
          </div>

          {/* Level info */}
          <div className="flex-1 min-w-0">
            <h3
              className={`
                font-display font-bold text-body-lg truncate
                ${isUnlocked ? "text-white" : "text-neutral-500"}
              `}
            >
              {badge?.name ?? `Level ${levelNumber}`}
            </h3>

            {isCompleted ? (
              <div className="mt-1">
                <StarDisplay stars={starRating} size="sm" />
                <p className="mt-1 font-body text-body-sm text-white/60">
                  {starsEarned} / {maxStars} stars
                </p>
              </div>
            ) : isUnlocked ? (
              <p className="mt-1 font-body text-body-md text-white/60">
                Tap to play!
              </p>
            ) : (
              <p className="font-body text-body-sm text-neutral-500">
                {level.lockReason === "subscription_required"
                  ? "Upgrade to unlock"
                  : "Complete previous level"}
              </p>
            )}
          </div>

          {/* Badge earned indicator */}
          {isCompleted && level.hasBadge && (
            <div className="relative">
              <Badge discipline={discipline} level={levelNumber} isEarned size="sm" />
              <div className="absolute -top-1 -right-1">
                <Sparkle className="w-4 h-4" style={{ color: glowColor }} delay={0.5} />
              </div>
            </div>
          )}

          {/* Arrow for unlocked levels */}
          {isUnlocked && !isCompleted && (
            <div className="text-white/60 animate-bounce">
              <ChevronRight className="w-6 h-6" />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (isUnlocked) {
    return (
      <Link
        to={`/play/${discipline}/${levelNumber}`}
        className="block focus:outline-none focus-visible:ring-4 focus-visible:ring-white/50 rounded-2xl"
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}

/**
 * Badge collection display
 */
function BadgeCollection({
  discipline,
  levels,
  badges,
}: {
  discipline: DisciplineSlug;
  levels: LevelProgress[];
  badges: readonly { readonly level: number; readonly name: string; readonly description: string }[];
}) {
  const earnedBadges = levels.filter((l) => l.hasBadge);
  const glowColor = GLOW_COLORS[discipline];

  return (
    <div
      className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10"
      style={{ boxShadow: `0 0 20px ${glowColor}10` }}
    >
      <h2 className="font-display font-bold text-display-sm text-white text-center mb-6">
        Badge Collection
      </h2>

      <div className="flex justify-center gap-4 flex-wrap">
        {badges.map((badge, index) => {
          const level = levels[index];
          const isEarned = level?.hasBadge ?? false;

          return (
            <div key={badge.level} className="flex flex-col items-center gap-2">
              <Badge
                discipline={discipline}
                level={badge.level}
                isEarned={isEarned}
                size="md"
              />
              <p
                className={`
                  font-body text-body-sm text-center max-w-[80px]
                  ${isEarned ? "text-sunshine-400" : "text-neutral-500"}
                `}
              >
                {badge.name}
              </p>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-center font-body text-body-md text-white/50">
        {earnedBadges.length} / {badges.length} badges earned
      </p>
    </div>
  );
}

/**
 * Themed background decorations for each discipline
 */
function ThemedBackground({ discipline }: { discipline: DisciplineSlug }) {
  switch (discipline) {
    case "math":
      return <StarfieldBackground />;
    case "spelling":
      return <GardenBackground />;
    case "patterns":
      return <RainbowBackground />;
    case "typing":
      return <KeyboardBackground />;
    case "programming":
      return <CircuitBackground />;
    default:
      return <StarfieldBackground />;
  }
}

// Background components for each discipline
function StarfieldBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Twinkling stars */}
      {Array.from({ length: 50 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${1 + Math.random() * 2}px`,
            height: `${1 + Math.random() * 2}px`,
            opacity: 0.3 + Math.random() * 0.7,
            animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        />
      ))}
      {/* Northern lights */}
      <div className="absolute inset-0 opacity-20">
        <div
          className="absolute top-0 left-0 w-full h-1/2"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(232, 160, 255, 0.15) 30%, rgba(155, 89, 182, 0.1) 60%, transparent 100%)",
          }}
        />
      </div>
    </div>
  );
}

function GardenBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating particles */}
      {Array.from({ length: 40 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${4 + Math.random() * 8}px`,
            height: `${4 + Math.random() * 8}px`,
            background: ["#90EE90", "#FFB8D0", "#B8F5C8", "#6BCB77"][
              Math.floor(Math.random() * 4)
            ],
            opacity: 0.2 + Math.random() * 0.4,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${4 + Math.random() * 3}s`,
          }}
        />
      ))}
    </div>
  );
}

function RainbowBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Rainbow gradient overlay */}
      <div
        className="absolute top-0 left-0 w-full h-full opacity-10"
        style={{
          background:
            "linear-gradient(135deg, #FF6B6B 0%, #FFB347 16%, #FFE66D 33%, #98D89B 50%, #74B9FF 66%, #A29BFE 83%, #FF6B6B 100%)",
          backgroundSize: "400% 400%",
          animation: "rainbowShift 15s ease infinite",
        }}
      />
      {/* Floating gems */}
      {Array.from({ length: 30 }).map((_, i) => {
        const colors = ["#FF6B6B", "#FFB347", "#FFE66D", "#98D89B", "#74B9FF", "#A29BFE"];
        return (
          <div
            key={i}
            className="absolute rotate-45 animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${6 + Math.random() * 10}px`,
              height: `${6 + Math.random() * 10}px`,
              background: colors[i % colors.length],
              opacity: 0.2 + Math.random() * 0.3,
              animationDelay: `${Math.random() * 4}s`,
              borderRadius: "2px",
            }}
          />
        );
      })}
      <style>{`
        @keyframes rainbowShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
}

function KeyboardBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating keys/scrolls */}
      {Array.from({ length: 25 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-lg animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${8 + Math.random() * 12}px`,
            height: `${8 + Math.random() * 12}px`,
            background: ["#4A90D9", "#7EB3E8", "#FFD700", "#3A75B0"][
              Math.floor(Math.random() * 4)
            ],
            opacity: 0.15 + Math.random() * 0.25,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${4 + Math.random() * 3}s`,
          }}
        />
      ))}
      {/* Magical sparkle stars */}
      {Array.from({ length: 20 }).map((_, i) => (
        <div
          key={`star-${i}`}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `twinkle ${2 + Math.random() * 2}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="#FFD700" opacity="0.3">
            <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
          </svg>
        </div>
      ))}
    </div>
  );
}

function CircuitBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Glowing particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: `${4 + Math.random() * 8}px`,
            height: `${4 + Math.random() * 8}px`,
            background: ["#FF8C42", "#00D4FF", "#E8C4A0", "#B87333"][
              Math.floor(Math.random() * 4)
            ],
            opacity: 0.2 + Math.random() * 0.3,
            animationDelay: `${Math.random() * 4}s`,
            animationDuration: `${3 + Math.random() * 3}s`,
          }}
        />
      ))}
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(90deg, #FF8C42 1px, transparent 1px),
            linear-gradient(180deg, #FF8C42 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />
    </div>
  );
}

/**
 * Sparkles for unlock animation
 */
function UnlockSparkles({ color }: { color: string }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible">
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i / 12) * 360;
        const distance = 60 + Math.random() * 40;
        const x = Math.cos((angle * Math.PI) / 180) * distance;
        const y = Math.sin((angle * Math.PI) / 180) * distance;

        return (
          <div
            key={i}
            className="absolute left-1/2 top-1/2"
            style={{
              animation: `sparkleOut 1.5s ease-out forwards`,
              animationDelay: `${i * 0.05}s`,
            }}
          >
            <div
              style={{
                transform: `translate(${x}px, ${y}px)`,
              }}
            >
              <Sparkle className="w-4 h-4" style={{ color }} delay={i * 0.1} />
            </div>
          </div>
        );
      })}
      <style>{`
        @keyframes sparkleOut {
          0% { opacity: 0; transform: scale(0) translate(-50%, -50%); }
          50% { opacity: 1; transform: scale(1) translate(-50%, -50%); }
          100% { opacity: 0; transform: scale(0.5) translate(-50%, -50%); }
        }
      `}</style>
    </div>
  );
}

/**
 * Sparkle icon
 */
function Sparkle({
  className,
  style,
  delay = 0,
}: {
  className?: string;
  style?: React.CSSProperties;
  delay?: number;
}) {
  return (
    <svg
      className={`${className} animate-sparkle`}
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ ...style, animationDelay: `${delay}s` }}
    >
      <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
    </svg>
  );
}

/**
 * Level icons for different disciplines
 */
function LevelIcon({
  type,
  className,
}: {
  type: string;
  className?: string;
}) {
  const icons: Record<string, JSX.Element> = {
    tower: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 10v11H5V10l7-8 7 8zM12 4l-5 5.5V19h10V9.5L12 4z" />
        <path d="M11 13h2v6h-2z" />
      </svg>
    ),
    castle: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 10v11H5V10l7-8 7 8zM12 4l-5 5.5V19h10V9.5L12 4z" />
        <path d="M11 13h2v6h-2z" />
        <circle cx="12" cy="2" r="1.5" fill="#FFD93D" />
      </svg>
    ),
    flower: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="4" />
        <circle cx="12" cy="5" r="3" opacity="0.8" />
        <circle cx="7" cy="9" r="3" opacity="0.8" />
        <circle cx="7" cy="15" r="3" opacity="0.8" />
        <circle cx="12" cy="19" r="3" opacity="0.8" />
        <circle cx="17" cy="15" r="3" opacity="0.8" />
        <circle cx="17" cy="9" r="3" opacity="0.8" />
      </svg>
    ),
    bloom: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="5" fill="#FFD93D" />
        <circle cx="12" cy="4" r="3.5" opacity="0.9" />
        <circle cx="6" cy="8" r="3.5" opacity="0.9" />
        <circle cx="6" cy="16" r="3.5" opacity="0.9" />
        <circle cx="12" cy="20" r="3.5" opacity="0.9" />
        <circle cx="18" cy="16" r="3.5" opacity="0.9" />
        <circle cx="18" cy="8" r="3.5" opacity="0.9" />
      </svg>
    ),
    gem: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L4 10l8 12 8-12-8-8zm0 3l5 5-5 7.5L7 10l5-5z" />
      </svg>
    ),
    crystal: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L4 10l8 12 8-12-8-8zm0 3l5 5-5 7.5L7 10l5-5z" />
        <path d="M12 8l3 3-3 4.5-3-4.5 3-3z" fill="#FFD93D" />
      </svg>
    ),
    key: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <rect x="7" y="4" width="10" height="16" rx="2" />
        <text x="12" y="14" textAnchor="middle" fontSize="8" fill="currentColor" opacity="0.5">
          A
        </text>
      </svg>
    ),
    scroll: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="3" width="12" height="18" rx="2" />
        <path d="M9 7h6M9 11h6M9 15h4" stroke="rgba(0,0,0,0.3)" strokeWidth="1.5" fill="none" />
        <circle cx="17" cy="7" r="3" fill="#FFD93D" />
      </svg>
    ),
    gear: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
      </svg>
    ),
    robot: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <rect x="6" y="8" width="12" height="12" rx="2" />
        <circle cx="9" cy="13" r="1.5" fill="#00D4FF" />
        <circle cx="15" cy="13" r="1.5" fill="#00D4FF" />
        <rect x="10" y="16" width="4" height="2" rx="1" opacity="0.5" />
        <rect x="11" y="4" width="2" height="4" />
        <circle cx="12" cy="3" r="2" />
      </svg>
    ),
  };

  return icons[type] || icons.tower;
}

/**
 * Lock icon
 */
function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z" />
    </svg>
  );
}

/**
 * Chevron right icon
 */
function ChevronRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export default DisciplineLevelMap;
